import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  XCircle,
  ArrowLeft,
  RefreshCw,
  MessageCircle,
  BookOpen
} from 'lucide-react';

const PaymentCancel = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Cancel Icon */}
          <div className="w-24 h-24 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-8">
            <XCircle className="w-12 h-12 text-orange-600 dark:text-orange-400" />
          </div>

          {/* Cancel Message */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Cancelled
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Your payment has been cancelled. No charges were made to your account.
          </p>

          {/* Information Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8 max-w-2xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              What Happened?
            </h3>
            
            <div className="text-left space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  You chose to cancel the payment process before completing it.
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  No charges were made to your payment method.
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  You can try again anytime or contact us if you need assistance.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8 max-w-2xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              What Would You Like to Do?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-azellar-teal transition-colors">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Try Again
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Return to the course page and complete your enrollment.
                </p>
              </div>
              
              <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-azellar-teal transition-colors">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Browse Courses
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Explore other courses that might interest you.
                </p>
              </div>
              
              <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-azellar-teal transition-colors">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Get Help
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Contact our support team if you need assistance.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <Link 
              to="/akademy/courses"
              className="btn-primary text-lg px-8 py-4 shadow-glow"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Courses
            </Link>
            
            <Link 
              to="/contact"
              className="btn-outline text-lg px-8 py-4"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Contact Support
            </Link>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl max-w-2xl mx-auto"
          >
            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
              Common Questions
            </h4>
            
            <div className="text-left space-y-3">
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  Will I be charged for cancelling?
                </p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  No, cancelling the payment process means no charges were made to your account.
                </p>
              </div>
              
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  Can I try to enroll again?
                </p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Yes, you can return to the course page and start the enrollment process again anytime.
                </p>
              </div>
              
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  What if I need help with payment?
                </p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Our support team is available to help with any payment questions or technical issues.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentCancel;